import { useCallback, useState } from "react";
import {
  crearMatricula,
  actualizarMatricula,
  eliminarMatricula,
} from "../../../../services/matriculasService";
import { buscarInscritos } from "../../../../services/estudiantesService";
import { normalizeMatricula } from "../adapters/matriculasAdapter";
import { showToast } from "../../../../utils/showToast";

const getErrorMessage = (err, fallback) => {
  if (err?.data?.message) return err.data.message;
  if (err?.message) return err.message;
  return fallback;
};

const initialForm = {
  estudiante_id: "",
  ciclo_id: "",
  promocion_id: "",
  curso_id: "",
  subtipo_curso_id: "",
  fecha_matricula: "",
  estado_matricula: "activa",
  estado_pago: "pendiente",
  modalidad: "presencial",
  referencia_pago: "",
  descuento_individual: 0,
  monto_resultante: "",
  activo: true,
  observaciones: "",
};

const initialEstudianteState = {
  documento: "",
  encontrado: null,
  buscando: false,
};

const validar = (form) => {
  if (!form.estudiante_id) return "Selecciona el estudiante.";
  const estado = (form.estado_matricula || "activa").toLowerCase();
  if (estado === "activa" && !form.promocion_id) return "Selecciona la promocion para activar la matricula.";
  if (!form.promocion_id && !form.curso_id) return "Selecciona el curso para reservar sin promocion.";
  return null;
};

export default function useMatriculaForm({ onCreated, onUpdated, onDeleted }) {
  const [modal, setModal] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editTab, setEditTab] = useState("datos");
  const [formNuevo, setFormNuevo] = useState(initialForm);
  const [formEditar, setFormEditar] = useState(initialForm);
  const [matriculaEditando, setMatriculaEditando] = useState(null);
  const [estudianteNuevo, setEstudianteNuevo] = useState(initialEstudianteState);
  const [estudianteEditar, setEstudianteEditar] = useState(initialEstudianteState);
  const [resultadosNuevo, setResultadosNuevo] = useState([]);

  const ejecutarBusqueda = useCallback(
    async (termino) => {
      const limpio = (termino || "").trim();
      const esNumerico = /^\d+$/.test(limpio);
      const esBusquedaNombre = !esNumerico && limpio.length >= 3;

      setEstudianteNuevo((prev) => ({ ...prev, buscando: true, encontrado: null }));
      setResultadosNuevo([]);

      try {
        let encontrado = null;

        const puedeConsultar = esNumerico || esBusquedaNombre;
        if (!puedeConsultar) {
          setEstudianteNuevo((prev) => ({ ...prev, buscando: false, encontrado: null }));
          return;
        }

        const resultados = await buscarInscritos(limpio);
        const lista = Array.isArray(resultados) ? resultados : [];
        if (lista.length === 0) {
          setEstudianteNuevo((prev) => ({ ...prev, buscando: false, encontrado: null }));
          showToast({
            type: "warning",
            message: "No se encontro un estudiante habilitado para matricula con ese nombre o documento.",
          });
          return;
        }
        if (lista.length === 1) {
          encontrado = lista[0];
        } else {
          setResultadosNuevo(lista);
          setEstudianteNuevo((prev) => ({ ...prev, buscando: false, encontrado: null }));
          return;
        }

        if (encontrado) {
          setEstudianteNuevo((prev) => ({ ...prev, buscando: false, encontrado }));
          setFormNuevo((prev) => ({ ...prev, estudiante_id: encontrado.id }));
          showToast({ type: "success", message: "Estudiante encontrado." });
        }
      } catch (err) {
        setEstudianteNuevo((prev) => ({ ...prev, buscando: false, encontrado: null }));
        setFormNuevo((prev) => ({ ...prev, estudiante_id: "" }));
        showToast({ type: "danger", message: err.message || "Error al buscar estudiante habilitado." });
      }
    },
    []
  );

  const setDocumentoNuevo = useCallback((documento) => {
    setEstudianteNuevo((prev) => ({ ...prev, documento, encontrado: null, buscando: false }));
    setFormNuevo((prev) => ({ ...prev, estudiante_id: "" }));
    setResultadosNuevo([]);
  }, []);

  const buscarEstudianteNuevo = useCallback(() => {
    const doc = estudianteNuevo.documento?.trim();
    const esNumerico = /^\d+$/.test(doc || "");
    const minChars = esNumerico ? 5 : 3;

    if (!doc || doc.length < minChars) return;

    ejecutarBusqueda(doc);
  }, [estudianteNuevo.documento, ejecutarBusqueda]);

  const seleccionarEstudiante = useCallback((estudiante, target = "nuevo") => {
    if (!estudiante) return;
    if (target === "nuevo") {
      setEstudianteNuevo((prev) => ({ ...prev, encontrado: estudiante, buscando: false, documento: estudiante.documento_identidad || prev.documento }));
      setFormNuevo((prev) => ({ ...prev, estudiante_id: estudiante.id }));
      setResultadosNuevo([]);
    } else {
      setEstudianteEditar((prev) => ({ ...prev, encontrado: estudiante, buscando: false, documento: estudiante.documento_identidad || prev.documento }));
      setFormEditar((prev) => ({ ...prev, estudiante_id: estudiante.id }));
    }
  }, []);

  const abrirCrear = useCallback(() => {
    setFormNuevo(initialForm);
    setEstudianteNuevo(initialEstudianteState);
    setResultadosNuevo([]);
    setModal("crear");
  }, []);

  const abrirEditar = useCallback((matricula, options = {}) => {
    if (!matricula) return;
    const { openModal = true } = options;
    const requestedTab = typeof options.tab === "string" ? options.tab.toLowerCase() : "";
    const parsed = normalizeMatricula(matricula);
    setMatriculaEditando(parsed);
    setEditTab(requestedTab === "entregables" ? "entregables" : "datos");
    setFormEditar({
      estudiante_id: parsed.estudianteId != null ? String(parsed.estudianteId) : "",
      ciclo_id: parsed.cicloId != null ? String(parsed.cicloId) : "",
      promocion_id: parsed.promocionId != null ? String(parsed.promocionId) : "",
      curso_id: parsed.cursoId != null ? String(parsed.cursoId) : "",
      subtipo_curso_id: parsed.subtipoCursoId != null ? String(parsed.subtipoCursoId) : "",
      fecha_matricula: parsed.fechaMatricula || "",
      estado_matricula: parsed.estadoMatricula || "activa",
      estado_pago: parsed.estadoPago || "pendiente",
      modalidad: parsed.modalidad || "presencial",
      referencia_pago: parsed.referenciaPago || "",
      descuento_individual: parsed.descuentoIndividual ?? 0,
      monto_resultante: parsed.montoResultante ?? "",
      activo: parsed.activo ?? true,
      observaciones: parsed.observaciones || "",
    });
    setEstudianteEditar({
      documento: "",
      encontrado: {
        id: parsed.estudianteId,
        nombres: parsed.estudianteNombres || "",
        apellidos: parsed.estudianteApellidos || "",
      },
      buscando: false,
    });
    if (openModal) setModal("editar");
  }, []);

  const abrirEliminar = useCallback((matricula) => {
    if (!matricula) return;
    setMatriculaEditando(normalizeMatricula(matricula));
    setModal("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setModal("");
    setMatriculaEditando(null);
    setFormNuevo(initialForm);
    setFormEditar(initialForm);
    setEstudianteNuevo(initialEstudianteState);
    setEstudianteEditar(initialEstudianteState);
    setResultadosNuevo([]);
  }, []);

  const handleCrear = useCallback(async () => {
    const validation = validar(formNuevo);
    if (validation) {
      showToast({ type: "warning", message: validation });
      return;
    }
    if (Number(formNuevo.descuento_individual) >= 100) {
      if (!window.confirm(
        "Estás aplicando un descuento del 100%.\n\nEl valor total será $0 y la matrícula quedará marcada como PAGADA sin recibir dinero real.\n\n¿Confirmas que esto es una CORTESÍA?"
      )) return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formNuevo,
        estudiante_id: formNuevo.estudiante_id ? Number(formNuevo.estudiante_id) : "",
        promocion_id: formNuevo.promocion_id ? Number(formNuevo.promocion_id) : "",
        curso_id: formNuevo.curso_id ? Number(formNuevo.curso_id) : null,
        subtipo_curso_id: formNuevo.subtipo_curso_id ? Number(formNuevo.subtipo_curso_id) : null,
        descuento_individual: Number(formNuevo.descuento_individual) || 0,
        activo: formNuevo.activo ?? true,
      };
      const created = await crearMatricula(payload);
      const normalized = normalizeMatricula(created || {});
      onCreated?.(normalized);
      showToast({ type: "success", message: "Matricula creada correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      const message = getErrorMessage(err, "No se pudo crear la matricula.");
      showToast({ type: "danger", message });
    } finally {
      setSaving(false);
    }
  }, [formNuevo, onCreated, cerrarModal]);

  const handleActualizar = useCallback(async (options = {}) => {
    const { closeOnSuccess = true } = options;
    if (!matriculaEditando) return;
    const validation = validar(formEditar);
    if (validation) {
      showToast({ type: "warning", message: validation });
      return null;
    }
    if (!formEditar.estudiante_id) {
      showToast({ type: "warning", message: "Selecciona el estudiante." });
      return null;
    }
    if (Number(formEditar.descuento_individual) >= 100) {
      if (!window.confirm(
        "Estás aplicando un descuento del 100%.\n\nEl valor total será $0 y la matrícula quedará marcada como PAGADA sin recibir dinero real.\n\n¿Confirmas que esto es una CORTESÍA?"
      )) return null;
    }
    setSaving(true);
    try {
      const payload = {
        ...formEditar,
        estudiante_id: formEditar.estudiante_id ? Number(formEditar.estudiante_id) : "",
        promocion_id: formEditar.promocion_id ? Number(formEditar.promocion_id) : "",
        curso_id: formEditar.curso_id ? Number(formEditar.curso_id) : null,
        subtipo_curso_id: formEditar.subtipo_curso_id ? Number(formEditar.subtipo_curso_id) : null,
        descuento_individual: Number(formEditar.descuento_individual) || 0,
        activo: formEditar.activo ?? true,
      };
      const updated = await actualizarMatricula(matriculaEditando.id, payload);
      const normalized = normalizeMatricula({ ...matriculaEditando, ...(updated || {}) });
      onUpdated?.(normalized);
      showToast({ type: "success", message: "Matricula actualizada correctamente." });
      if (closeOnSuccess) cerrarModal();
      return normalized;
    } catch (err) {
      console.error(err);
      const message = getErrorMessage(err, "No se pudo actualizar la matricula.");
      showToast({ type: "danger", message });
      return null;
    } finally {
      setSaving(false);
    }
  }, [formEditar, matriculaEditando, onUpdated, cerrarModal]);

  const handleEliminar = useCallback(async () => {
    if (!matriculaEditando) return;
    setDeleting(true);
    try {
      await eliminarMatricula(matriculaEditando.id);
      onDeleted?.(matriculaEditando);
      showToast({ type: "success", message: "Matricula eliminada correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      const message = getErrorMessage(err, "No se pudo eliminar la matricula.");
      showToast({ type: "danger", message });
    } finally {
      setDeleting(false);
    }
  }, [matriculaEditando, onDeleted, cerrarModal]);

  return {
    modal,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    matriculaEditando,
    saving,
    deleting,
    editTab,
    abrirCrear,
    abrirEditar,
    abrirEliminar,
    cerrarModal,
    handleCrear,
    handleActualizar,
    handleEliminar,
    resultadosNuevo,
    seleccionarEstudiante,
    estudianteNuevo,
    estudianteEditar,
    buscarEstudianteNuevo,
    setDocumentoNuevo,
  };
}

